import React, { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate, useNavigate } from 'react-router-dom';
import { AnimatePresence, MotionConfig, motion } from 'framer-motion';
import { Capacitor, type PluginListenerHandle } from '@capacitor/core';
import { PageTransition } from './components/PageTransition';
import { BottomNav } from './components/BottomNav';
import { Login } from './components/Login';
import { Onboarding } from './components/Onboarding';
import { ResourceDownloader } from './components/ResourceDownloader';
import { auth, analytics } from './firebase';
import { GalleryService } from './services/galleryService';
import { supabase } from './supabaseClient';
import { favoritesService } from './services/FavoritesService';

import { ErrorBoundary } from './components/ErrorBoundary';
import { PrivacyPolicy } from './components/PrivacyPolicy';
import { TemplateCache } from './services/TemplateCache';
import { LoadingScreen } from './components/LoadingScreen';
import { SoundEffectsService, type SoundEffectPreset } from './services/SoundEffectsService';
import { ResetPassword } from './components/ResetPassword';
import { LandingPage } from './components/LandingPage';
import { SeoPage } from './components/SeoPage';
import { FeedbackHost } from './components/ui/FeedbackHost';
import { seoPageMap, seoPages, type SeoFaq } from './content/seoPages';
import { notify } from './services/UIFeedbackService';
import { APP_FADE, APP_SPRING } from './utils/motion';

const Dashboard = lazy(async () => {
  const module = await import('./components/Dashboard');
  return { default: module.Dashboard };
});
const TemplateLibrary = lazy(async () => {
  const module = await import('./components/TemplateLibrary');
  return { default: module.TemplateLibrary };
});
const Collection = lazy(async () => {
  const module = await import('./components/Collection');
  return { default: module.Collection };
});
const Profile = lazy(async () => {
  const module = await import('./components/Profile');
  return { default: module.Profile };
});
const ARCanvas = lazy(async () => {
  const module = await import('./components/ARCanvas');
  return { default: module.ARCanvas };
});
const TextArt = lazy(async () => {
  const module = await import('./components/TextArt');
  return { default: module.TextArt };
});
const AIGenerator = lazy(async () => {
  const module = await import('./components/AIGenerator');
  return { default: module.AIGenerator };
});
const LessonsLibrary = lazy(async () => {
  const module = await import('./components/LessonsLibrary');
  return { default: module.LessonsLibrary };
});
const LessonPlayer = lazy(async () => {
  const module = await import('./components/LessonPlayer');
  return { default: module.LessonPlayer };
});
const DigitalCanvas = lazy(async () => {
  const module = await import('./components/DigitalCanvas');
  return { default: module.DigitalCanvas };
});
const CommunityFeed = lazy(async () => {
  const module = await import('./components/CommunityFeed');
  return { default: module.CommunityFeed };
});
const UserProfile = lazy(async () => {
  const module = await import('./components/UserProfile');
  return { default: module.UserProfile };
});
const EditProfile = lazy(async () => {
  const module = await import('./components/EditProfile');
  return { default: module.EditProfile };
});
const SavedPosts = lazy(async () => {
  const module = await import('./components/SavedPosts');
  return { default: module.SavedPosts };
});
const Favorites = lazy(async () => {
  const module = await import('./components/Favorites');
  return { default: module.Favorites };
});
const BadgesPage = lazy(async () => {
  const module = await import('./components/BadgesPage');
  return { default: module.BadgesPage };
});
const QuickStartTutorial = lazy(async () => {
  const module = await import('./components/QuickStartTutorial');
  return { default: module.QuickStartTutorial };
});

const GUEST_USER = {
  uid: 'guest-user-123',
  displayName: 'Guest Artist',
  email: null,
  photoURL: null,
  isPro: false,
};

const isGuestModeEnabled = () => localStorage.getItem('guestMode') === 'true';

const shouldShowOnboarding = () => !localStorage.getItem('hasSeenOnboarding_v2');

const shouldShowTutorial = () => (
  !localStorage.getItem('tutorial_completed') &&
  !localStorage.getItem('tutorial_skipped') &&
  !localStorage.getItem('guestMode')
);

const getInitialUser = () => auth.currentUser ?? (isGuestModeEnabled() ? GUEST_USER : null);
const isWebShowcaseMode =
  Capacitor.getPlatform() === 'web' &&
  import.meta.env.VITE_WEB_SHOWCASE_MODE === 'true';
const isNativePlatform = Capacitor.getPlatform() !== 'web';
const SITE_ORIGIN = 'https://ardrawingmaster.online';

type SeoConfig = {
  canonicalUrl: string;
  description: string;
  robots: string;
  structuredData?: object[];
  title: string;
};

const LANDING_SEO: SeoConfig = {
  canonicalUrl: `${SITE_ORIGIN}/`,
  description:
    'Download AR Drawing Master APK from the official website, use an Android AR tracing app for drawing on real paper, and explore templates, AI sketch ideas, and guided practice workflows.',
  robots: 'index,follow',
  title: 'AR Drawing Master APK Download | AR Tracing App for Android',
};

const PRIVACY_SEO: SeoConfig = {
  canonicalUrl: `${SITE_ORIGIN}/privacy`,
  description: 'Privacy policy for the AR Drawing Master website and Android app.',
  robots: 'index,follow',
  title: 'Privacy Policy | AR Drawing Master',
};

const APP_ROUTE_SEO: SeoConfig = {
  canonicalUrl: `${SITE_ORIGIN}/`,
  description: 'Official AR Drawing Master website and Android app resources.',
  robots: 'noindex,follow',
  title: 'AR Drawing Master App',
};

const appStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  applicationCategory: 'MultimediaApplication',
  downloadUrl: `${SITE_ORIGIN}/download`,
  name: 'AR Drawing Master',
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'USD',
  },
  operatingSystem: 'Android',
  url: SITE_ORIGIN,
};

const websiteStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'AR Drawing Master',
  url: SITE_ORIGIN,
};

const createBreadcrumbData = (title: string, pathname: string) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    {
      '@type': 'ListItem',
      item: SITE_ORIGIN,
      name: 'Home',
      position: 1,
    },
    {
      '@type': 'ListItem',
      item: `${SITE_ORIGIN}${pathname}`,
      name: title,
      position: 2,
    },
  ],
});

const createFaqData = (faqs: SeoFaq[]) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
    name: faq.question,
  })),
});

const normalizeSeoPath = (pathname: string) => {
  if (!pathname || pathname === '/') {
    return '/';
  }

  const trimmed = pathname.replace(/\/+$/, '');
  return trimmed || '/';
};

const getRouteSeo = (pathname: string, indexRoot: boolean): SeoConfig => {
  const normalizedPath = normalizeSeoPath(pathname);
  const marketingPage = seoPageMap.get(normalizedPath);

  if (marketingPage) {
    return {
      canonicalUrl: marketingPage.canonicalUrl,
      description: marketingPage.description,
      robots: 'index,follow',
      structuredData: [
        websiteStructuredData,
        appStructuredData,
        createBreadcrumbData(marketingPage.shortLabel, marketingPage.path),
        ...(marketingPage.faqs ? [createFaqData(marketingPage.faqs)] : []),
      ],
      title: marketingPage.title,
    };
  }

  if (normalizedPath === '/privacy') {
    return {
      ...PRIVACY_SEO,
      structuredData: [
        websiteStructuredData,
        appStructuredData,
        createBreadcrumbData('Privacy', '/privacy'),
      ],
    };
  }

  if (normalizedPath === '/' && indexRoot) {
    return {
      ...LANDING_SEO,
      structuredData: [websiteStructuredData, appStructuredData],
    };
  }

  return APP_ROUTE_SEO;
};

const setMetaContent = (id: string, value: string) => {
  const element = document.getElementById(id);
  if (element instanceof HTMLMetaElement) {
    element.content = value;
  }
};

const setCanonicalHref = (href: string) => {
  const element = document.getElementById('canonical-link');
  if (element instanceof HTMLLinkElement) {
    element.href = href;
  }
};

const applySeoConfig = (seo: SeoConfig) => {
  if (typeof document === 'undefined') {
    return;
  }

  document.title = seo.title;
  setMetaContent('meta-description', seo.description);
  setMetaContent('meta-robots', seo.robots);
  setMetaContent('og-title', seo.title);
  setMetaContent('og-description', seo.description);
  setMetaContent('og-url', seo.canonicalUrl);
  setMetaContent('twitter-title', seo.title);
  setMetaContent('twitter-description', seo.description);
  setCanonicalHref(seo.canonicalUrl);

  const structuredDataElement = document.getElementById('structured-data');
  if (structuredDataElement instanceof HTMLScriptElement) {
    structuredDataElement.textContent = JSON.stringify(seo.structuredData ?? []);
  }
};

const useRouteSeo = (indexRoot: boolean) => {
  const location = useLocation();

  useEffect(() => {
    applySeoConfig(getRouteSeo(location.pathname, indexRoot));
  }, [indexRoot, location.pathname]);
};

const parseIncomingAppUrl = (rawUrl: string): URL | null => {
  try {
    if (rawUrl.startsWith('com.ardrawing.app://')) {
      return new URL(rawUrl.replace('com.ardrawing.app://', 'http://localhost/'));
    }

    return new URL(rawUrl);
  } catch (error) {
    console.warn('[DeepLink] Failed to parse URL:', rawUrl, error);
    return null;
  }
};

const WebShowcaseContent: React.FC = () => {
  useRouteSeo(true);

  return (
    <AnimatePresence mode="wait" initial={false}>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        {seoPages.map((page) => (
          <Route
            key={page.path}
            path={page.path}
            element={<PageTransition><SeoPage page={page} /></PageTransition>}
          />
        ))}
        <Route path="/privacy" element={<PageTransition><PrivacyPolicy /></PageTransition>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AnimatePresence>
  );
};

const AppContent: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(getInitialUser);
  const [loading, setLoading] = useState(() => !isGuestModeEnabled());
  const [showOnboarding, setShowOnboarding] = useState(shouldShowOnboarding);
  const [showTutorial, setShowTutorial] = useState(shouldShowTutorial);

  useRouteSeo(!user);

  // EFFECT 1: One-time initialization (runs only once on mount)
  useEffect(() => {
    let isMounted = true;
    let adInitTimer: number | undefined;

    if (isNativePlatform) {
      adInitTimer = window.setTimeout(() => {
        import('./services/AdMobService').then(({ adMobService }) => {
          if (isMounted) {
            void adMobService.initialize();
          }
        }).catch((error) => {
          console.error('Failed to load AdMob service:', error);
        });
      }, 1500);
    }

    // Initialize Purchase Service
    import('./services/PurchaseService').then(({ PurchaseService }) => {
      PurchaseService.initialize();
    }).catch((error) => {
      console.error('Failed to load Purchase service:', error);
    });

    // Initialize Cloud Gallery Sync (skip for guests)
    if (localStorage.getItem('guestMode') !== 'true') {
      GalleryService.syncFromCloud().catch((error) => {
        console.error('Failed to init gallery sync:', error);
      });
    }

    // Initialize Analytics
    try {
      analytics.initialize();
      analytics.logEvent('app_open', { platform: (window as { Capacitor?: { getPlatform?: () => string } }).Capacitor?.getPlatform?.() || 'web' });
    } catch (error) {
      console.error('Failed to load Analytics service:', error);
    }

    // Initialize Template Cache
    TemplateCache.initialize().catch((error) => {
      console.error('Failed to initialize template cache:', error);
    });

    return () => {
      isMounted = false;
      if (adInitTimer !== undefined) {
        window.clearTimeout(adInitTimer);
      }
    };
  }, []);

  // EFFECT: Track Screen Views
  useEffect(() => {
    analytics.setScreenName(location.pathname);
    analytics.logEvent('screen_view', { screen_name: location.pathname });
  }, [location]);

  // EFFECT: Prime and play UI sound effects for interactive controls outside the canvas
  useEffect(() => {
    const primeAudio = () => {
      SoundEffectsService.prime();
    };

    const handleInteractionSound = (event: MouseEvent) => {
      if (location.pathname === '/canvas') {
        return;
      }

      const target = event.target;
      if (!(target instanceof HTMLElement)) {
        return;
      }

      const interactive = target.closest<HTMLElement>('[data-sound],button,a,[role="button"]');
      if (!interactive || interactive.closest('[data-disable-sound="true"]')) {
        return;
      }

      if (interactive instanceof HTMLButtonElement && interactive.disabled) {
        return;
      }

      const preset = interactive.dataset.sound as SoundEffectPreset | 'none' | undefined;
      if (preset === 'none') {
        return;
      }

      SoundEffectsService.play(preset ?? 'tap');
    };

    document.addEventListener('pointerdown', primeAudio, { passive: true });
    document.addEventListener('click', handleInteractionSound, true);

    return () => {
      document.removeEventListener('pointerdown', primeAudio);
      document.removeEventListener('click', handleInteractionSound, true);
    };
  }, [location.pathname]);

  // EFFECT 2: Initialize Notifications (runs once)
  useEffect(() => {
    import('./services/NotificationService').then(async ({ NotificationService }) => {
      try {
        const granted = await NotificationService.requestPermissions();
        if (granted) {
          await NotificationService.createChannel();
          await NotificationService.scheduleDailyReminder();
          await NotificationService.addListeners((path, state) => {
            if (state === undefined) {
              navigate(path);
              return;
            }

            navigate(path, { state });
          });
        }
      } catch (error) {
        console.error('Failed to initialize notifications:', error);
      }
    }).catch((error) => {
      console.error('Failed to load Notification service:', error);
    });
  }, [navigate]);

  // EFFECT 3: Redirect result check (runs only once)
  useEffect(() => {
    // Check for Google Sign-In redirect result
    const checkRedirectResult = async () => {
      try {
        const user = await auth.getRedirectResult();
        if (user) {
          console.log('Google Sign-In successful via redirect:', user);
          // User is now signed in, auth state listener will handle navigation
        }
      } catch (error) {
        console.error('Redirect result error:', error);
      }
    };

    checkRedirectResult();
  }, [navigate]);

  // EFFECT 4.5: Handle deep link OAuth callback
  useEffect(() => {
    let cancelled = false;
    let appUrlOpenListener: PluginListenerHandle | null = null;

    import('@capacitor/app').then(async ({ App }) => {
      if (cancelled) {
        return;
      }

      const listener = await App.addListener('appUrlOpen', async (event) => {
          console.log('[DeepLink] ========================================');
          console.log('[DeepLink] Full URL:', event.url);

          const incomingUrl = parseIncomingAppUrl(event.url);
          const incomingPath = incomingUrl?.pathname?.toLowerCase() ?? '';

          // Handle shared community links before auth flows.
          if (incomingPath === '/community') {
            const postId = incomingUrl?.searchParams.get('post') || incomingUrl?.searchParams.get('postId');
            const inviterId = incomingUrl?.searchParams.get('ref') || undefined;

            analytics.logEvent('invite_link_opened', {
              invite_type: 'community_post',
              post_id: postId || 'unknown',
              inviter_id: inviterId || 'unknown'
            });

            if (postId) {
              navigate(`/community?post=${encodeURIComponent(postId)}`, { replace: false });
            } else {
              navigate('/community', { replace: false });
            }
            return;
          }

          if (event.url.includes('google-auth') || event.url.includes('facebook-auth')) {
            console.log('[DeepLink] OAuth callback detected');

            try {
              try {
                const { Browser } = await import('@capacitor/browser');
                await Browser.close();
              } catch (browserError) {
                console.log('[DeepLink] Browser close skipped:', browserError);
              }

              // Log ALL parts of the URL
              console.log('[DeepLink] URL length:', event.url.length);

              let code = null;
              let accessToken = null;
              let refreshToken = null;

              // 1. Try extracting CODE (PKCE Flow)
              // Method 1A: Query string
              try {
                const url = new URL(event.url.replace('com.ardrawing.app://', 'http://localhost/'));
                code = url.searchParams.get('code');
              } catch (e) { console.log('[DeepLink] Query parse error:', e); }

              // Method 1B: Hash fragment
              if (!code && event.url.includes('#')) {
                const hashPart = event.url.split('#')[1];
                const params = new URLSearchParams(hashPart);
                code = params.get('code');
              }

              // Method 1C: Regex
              if (!code) {
                const codeMatch = event.url.match(/[?&#]code=([^&]+)/);
                if (codeMatch) code = codeMatch[1];
              }

              // 2. Try extracting TOKENS (Implicit Flow Fallback)
              if (!code) {
                console.log('[DeepLink] Code missing, checking for tokens...');
                try {
                  // Normalize URL
                  const urlStr = event.url.replace('com.ardrawing.app://', 'http://localhost/');
                  const urlObj = new URL(urlStr);

                  // Check query params
                  accessToken = urlObj.searchParams.get('access_token');
                  refreshToken = urlObj.searchParams.get('refresh_token');

                  // Check hash params
                  if (!accessToken && urlStr.includes('#')) {
                    const hashStr = urlStr.split('#')[1];
                    const hashParams = new URLSearchParams(hashStr);
                    accessToken = hashParams.get('access_token');
                    refreshToken = hashParams.get('refresh_token');
                  }
                } catch (e) {
                  console.log('[DeepLink] Token extraction failed:', e);
                }
              }

              console.log('[DeepLink] Final Code:', !!code);
              console.log('[DeepLink] Final Token:', !!accessToken);

              if (code) {
                console.log('[DeepLink] Exchanging authorization code for session...');
                const { data, error } = await supabase.auth.exchangeCodeForSession(code);

                if (error) {
                  console.error('[DeepLink] Code exchange error:', error);
                  notify.error(`Login failed: ${error.message}`);
                  return;
                }

                console.log('[DeepLink] ✅ Session obtained via CODE! User:', data.user?.email);
                window.location.href = '/';

              } else if (accessToken && refreshToken) {
                console.log('[DeepLink] Setting session via TOKENS...');
                const { data, error } = await supabase.auth.setSession({
                  access_token: accessToken,
                  refresh_token: refreshToken
                });

                if (error) {
                  console.error('[DeepLink] Token session error:', error);
                  notify.error(`Login failed: ${error.message}`);
                  return;
                }
                console.log('[DeepLink] ✅ Session obtained via TOKENS! User:', data.user?.email);
                window.location.href = '/';

              } else {
                console.error('[DeepLink] No auth code OR tokens found');
                console.error('[DeepLink] RAW URL:', event.url);
                notify.error('Login failed. Please try again or contact support.');
              }
            } catch (e) {
              console.error('[DeepLink] Exception:', e);
              notify.error('Login error. Please try again.');
            }
          }
        });

      if (cancelled) {
        void listener.remove();
        return;
      }

      appUrlOpenListener = listener;
    }).catch((e) => console.log('App plugin not available:', e));

    return () => {
      cancelled = true;
      if (appUrlOpenListener) {
        void appUrlOpenListener.remove();
        appUrlOpenListener = null;
      }
    };
  }, [navigate]);

  // EFFECT 4: Auth state listener (runs only once)
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (u) => {
      // 1. Immediate update with basic info (FAST)
      if (u) {
        // Always update with fresh auth data from the callback parameter
        setUser({
          uid: u.uid,
          email: u.email || '',
          displayName: u.displayName || 'Artist',
          photoURL: u.photoURL,
          isPro: u.isPro || false // Use isPro from profile fetch
        });
        localStorage.removeItem('guestMode');

        // Optional: Set User ID in Analytics
        analytics.setUserId(u.uid);

        // Trigger Cloud Sync
        GalleryService.syncFromCloud().catch((error) => {
          console.error('Failed to sync cloud gallery:', error);
        });
      } else if (localStorage.getItem('guestMode') !== 'true') {
        setUser(null);
      }

      favoritesService.hydrateForCurrentUser().catch((error) => {
        console.error('Failed to hydrate favorites:', error);
      });

      // ALWAYS turn off loading immediately
      setLoading(false);
    });

    const timeout = setTimeout(() => {
      setLoading((prev) => {
        if (prev) {
          console.warn("Auth listener timed out, forcing app load.");
          return false;
        }
        return prev;
      });
    }, 4000); // Reduced timeout to 4s

    return () => {
      unsubscribe();
      clearTimeout(timeout);
    };
  }, []);


  // EFFECT 6: Support Popup Listener
  const [showSupportPopup, setShowSupportPopup] = useState(false);
  useEffect(() => {
    const handleSupportPopup = () => setShowSupportPopup(true);
    window.addEventListener('SHOW_SUPPORT_POPUP', handleSupportPopup);
    return () => window.removeEventListener('SHOW_SUPPORT_POPUP', handleSupportPopup);
  }, []);

  // EFFECT 5: Android Back Button (runs on route change for proper handling)
  useEffect(() => {
    let backButtonListener: { remove: () => Promise<void> } | undefined;
    let mounted = true;

    const setupBackButton = async () => {
      const { App: CapacitorApp } = await import('@capacitor/app');
      const { adMobService } = await import('./services/AdMobService');

      // Don't set listener if component unmounted during async operations
      if (!mounted) return;

      backButtonListener = await CapacitorApp.addListener('backButton', async () => {
        if (location.pathname === '/' || location.pathname === '/login') {
          // Show exit ad before closing
          await adMobService.showExitAd();
          CapacitorApp.exitApp();
        } else {
          window.history.back();
        }
      });
    };
    setupBackButton();

    return () => {
      mounted = false;
      if (backButtonListener) {
        backButtonListener.remove();
      }
    };
  }, [location.pathname]);

  const handleOnboardingComplete = () => {
    localStorage.setItem('hasSeenOnboarding_v2', 'true');
    setShowOnboarding(false);
  };
  const isPublicRoute = location.pathname === '/' || location.pathname === '/login' || location.pathname === '/reset-password';
  const shouldRenderFirstRunOnboarding =
    showOnboarding &&
    !user &&
    location.pathname !== '/reset-password';

  // First-install onboarding should appear after the opening animation and before login.
  if (shouldRenderFirstRunOnboarding) {
    return <Onboarding onComplete={handleOnboardingComplete} />;
  }

  if (showTutorial && !loading && user) {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <QuickStartTutorial onComplete={() => setShowTutorial(false)} />
      </Suspense>
    );
  }

  if (loading) return <LoadingScreen />;

  if (!user && !isPublicRoute) {
    return <Navigate to="/login" replace />;
  }

  if (user && location.pathname === '/login') {
    return <Navigate to="/" replace />;
  }

  if (!user && location.pathname === '/') {
    if (isNativePlatform) {
      return <Navigate to="/login" replace />;
    }
    return <LandingPage />;
  }

  const showNav = !!user && (
    ['/', '/gallery', '/profile', '/templates'].includes(location.pathname) ||
    location.pathname.startsWith('/templates/')
  );

  return (
    <MotionConfig
      reducedMotion="user"
      transition={APP_SPRING}
    >
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex justify-center transition-theme mesh-gradient-bg">
        <div className="w-full max-w-md bg-gray-50 dark:bg-gray-900 min-h-screen shadow-2xl relative font-sans text-gray-900 dark:text-gray-100 transition-theme">


        {/* Background Resource Downloader */}
        <ResourceDownloader />

        {/* Global toasts + confirm modal */}
        <FeedbackHost />

        {/* Support Popup */}
        {showSupportPopup && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-sm w-full text-center shadow-xl animate-in fade-in zoom-in duration-300">
              <div className="w-16 h-16 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">🎁</span>
              </div>
              <h3 className="text-xl font-bold mb-2">Thanks for your support!</h3>
              <p className="text-gray-600 dark:text-gray-300 mb-6 text-sm">
                Ads help us keep this app free and add new templates regularly.
                <br /><br />
                Want to remove ads? Try <strong>Pro</strong> for 15 days free!
              </p>
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => {
                    setShowSupportPopup(false);
                    // Navigate to profile or subscription modal if implemented
                    navigate('/profile');
                  }}
                  className="w-full py-3 bg-linear-to-r from-purple-600 to-pink-600 text-white rounded-xl font-semibold shadow-lg shadow-purple-500/30 active:scale-95 transition-transform"
                >
                  Remove Ads (Free Trial)
                </button>
                <button
                  onClick={() => setShowSupportPopup(false)}
                  className="w-full py-3 text-gray-500 font-medium hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        <Suspense fallback={<LoadingScreen />}>
          <AnimatePresence mode="wait" initial={false}>
            <Routes location={location} key={location.pathname}>
              <Route path="/login" element={<PageTransition><Login /></PageTransition>} />
              <Route path="/" element={<PageTransition><Dashboard key={location.key} /></PageTransition>} />
              <Route path="/templates" element={<PageTransition><TemplateLibrary key={location.key} /></PageTransition>} />
              <Route path="/templates/:categoryId" element={<PageTransition><TemplateLibrary key={location.key} /></PageTransition>} />
              <Route path="/gallery" element={<PageTransition><Collection key={location.key} /></PageTransition>} />
              <Route path="/profile" element={<PageTransition><Profile /></PageTransition>} />
              <Route path="/canvas" element={<ARCanvas key={location.key} />} /> {/* Don't animate Canvas to avoid camera re-init issues */}
              <Route path="/text-art" element={<PageTransition><TextArt /></PageTransition>} />
              <Route path="/ai-generator" element={<PageTransition><AIGenerator /></PageTransition>} />
              <Route path="/lessons" element={<PageTransition><LessonsLibrary /></PageTransition>} />
              <Route path="/lessons/:lessonId" element={<PageTransition><LessonPlayer /></PageTransition>} />
              <Route path="/digital-canvas" element={<PageTransition><DigitalCanvas /></PageTransition>} />
              <Route path="/community" element={<PageTransition><CommunityFeed /></PageTransition>} />
              <Route path="/user/:userId" element={<PageTransition><UserProfile /></PageTransition>} />
              <Route path="/edit-profile" element={<PageTransition><EditProfile /></PageTransition>} />
              <Route path="/favorites" element={<PageTransition><Favorites /></PageTransition>} />
              <Route path="/saved" element={<PageTransition><SavedPosts /></PageTransition>} />
              <Route path="/privacy" element={<PageTransition><PrivacyPolicy /></PageTransition>} />
              <Route path="/badges" element={<PageTransition><BadgesPage /></PageTransition>} />
              <Route path="/reset-password" element={<PageTransition><ResetPassword /></PageTransition>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </AnimatePresence>
        </Suspense>

        <AnimatePresence initial={false}>
          {showNav && (
            <motion.div
              key="bottom-nav"
              className="bottom-nav-wrapper"
              initial={{ opacity: 0, y: 16, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 12, scale: 0.98 }}
              transition={APP_FADE}
            >
              <BottomNav />
            </motion.div>
          )}
        </AnimatePresence>
        </div>
      </div>
    </MotionConfig>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <Router>
        {isWebShowcaseMode ? <WebShowcaseContent /> : <AppContent />}
      </Router>
    </ErrorBoundary>
  );
}

export default App;
